import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useState,
  useEffect,
  SetStateAction,
  Dispatch,
  useCallback,
} from "react";
import { getVariantImageSource } from "@/app/components/pages-components/product-details-page/details-belpers/product-attributes/helpers/functions";
import {
  IProductMinimizedData,
  IProductChildMinimizedData,
} from "@/app/components/pages-components/product-details-page/details-belpers/product-attributes/helpers/interfaces";
import { intersectionsArray } from "@/app/components/common-addons-builder/addon-functions";
import { useGetCartQuery } from "@/api/AppApi/services/cart";
import { ICartItemType, ICartItem_Product } from "@/api/AppApi/services/cart/interfaces/cart-interfaces";

import { getIsService } from "../product-box-helpers/functions";
import {
  ProductPreviewTypes,
  unit,
} from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import { compareAddOnsArrays } from "@/hooks/product-has-attrs/product-has-attrs-helpers/functions";
import { ReviewVisibility } from "@/api/AppApi/services/company/interfaces/ICompany";
import { useQueryClient } from "@tanstack/react-query";
import { getCompanyKey } from "@/api/AppApi/common-interfaces/quiries-keys/queries-keys";
import { useLangCookie } from "@/providers/langProvider";
interface ProductBoxContextType {
  selectedChild: IProductChildMinimizedData | IProductMinimizedData;
  selectedChildIndex: number | null;
  setSelectedChildIndex: (index: number | null) => void;
  isSelectedChild: boolean;
  product: IProductMinimizedData;
  attrs_values: number[];
  quantity: number;
  setQuantity: Dispatch<SetStateAction<number>>;
  addedToCart: boolean;
  setAddedToCart: Dispatch<SetStateAction<boolean>>;
  quick_variant_selection?: boolean;
  showselectAttrsPopup: boolean;
  selectedAttributes: number[];
  setSelectedAttributes: Dispatch<SetStateAction<number[]>>;
  outOfStock: boolean;
  isDisabled: boolean;
  viewDetails: boolean;
  mustViewDetails?: boolean;
  currentProductInCart: {
    quantity: number;
    addedToCart: boolean;
    id?: number;
  };
  allow_user_request_stock_availability_reminder?: boolean;
  is_favorite?: boolean;
  showRate?: boolean;
}

const ProductBoxContext = createContext<ProductBoxContextType | undefined>(
  undefined,
);

export const getProductFromCart = (
  cartItems: ICartItemType[],
  product: IProductMinimizedData | IProductChildMinimizedData,
  enable_multiple_unit_add_to_cart?: boolean,
) => {
  const index = (cartItems ?? [])?.findIndex(
    (item) => {
      const pItem = item as ICartItem_Product;
      return pItem.inner_id === product?.id &&
        compareAddOnsArrays(pItem.addons, []) &&
        !pItem.note &&
        (!enable_multiple_unit_add_to_cart || pItem.others?.unit_id === Number(product.unit_id));
    }
  );
  if (index < 0)
    return {
      quantity: 0,
      addedToCart: false,
      id: undefined,
    };
  return {
    quantity: cartItems[index].quantity,
    addedToCart: true,
    id: cartItems[index].id,
  };
};
export const ProductBoxContextProvider: React.FC<{
  children: ReactNode;
  product: IProductMinimizedData;
  preview_type: ProductPreviewTypes;
  units: unit[];
  type: string;
  bundleSetSelectedChildIndex?: (index: number | null) => void;
}> = ({
  children,
  product,
  preview_type,
  units,
  type,
  bundleSetSelectedChildIndex,
}) => {
  const queryClient = useQueryClient();
  const langCookie = useLangCookie();
  const companyData = queryClient.getQueryData<any>(getCompanyKey(langCookie));
  const {
    quick_variant_selection,
    show_all_product_units,
    allow_user_request_stock_availability_reminder,
    product_reviews_visibility,
  } = companyData?.result?.theme?.components?.product ?? {};

  const enable_multiple_unit_add_to_cart =
    companyData?.result?.theme?.components?.cart?.enable_multiple_unit_add_to_cart;

  const [selectedChildIndex, setSelectedChildIndex] = useState<number | null>(
    null,
  );
  const [selectedAttributes, setSelectedAttributes] = useState<number[]>([]);
  const [quantity, setQuantity] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);
  const selectedChild = useMemo(() => {
    if (selectedChildIndex === null || selectedChildIndex === -1) {
      // Partial selection: show the first matching variant's images, but keep
      // the product's own data (price, stock, cart) until a variant is exact.
      const imageSource = getVariantImageSource(product, selectedAttributes);
      return imageSource === product
        ? product
        : { ...product, images: imageSource.images };
    }

    const child = product?.children?.[selectedChildIndex];
    if (!child || !product) return product;

    return {
      ...child,
      // discount_amount: child.discount_amount ?? product.discount_amount,
      discount_type: child.discount_type ?? product.discount_type,
      discount: child.discount ?? product.discount,
      is_new: child.is_new ?? product.is_new,
      images: getVariantImageSource(product, selectedAttributes, child).images,
      price: child.price ?? product.price,
      price_without_discount:
        child.price_without_discount ?? product.price_without_discount,
      selling_prices: child.selling_prices ?? product.selling_prices,
      has_required_addons:
        child.has_required_addons ?? product.has_required_addons,
      has_discount: child.has_discount ?? product.has_discount,
    };
  }, [product, selectedChildIndex, selectedAttributes]);

  const { data: currentProductInCart = { quantity: 0, addedToCart: false, id: undefined }, isFetching: isCartFetching } = useGetCartQuery({
    select: useCallback((data: any) => getProductFromCart(data?.result?.items ?? [], selectedChild, enable_multiple_unit_add_to_cart), [selectedChild, enable_multiple_unit_add_to_cart])
  });

  useEffect(() => {
    if (isCartFetching) return;
    setAddedToCart(currentProductInCart.addedToCart);
    setQuantity(currentProductInCart.quantity);
  }, [isCartFetching, currentProductInCart]);

  const isService = getIsService(preview_type as ProductPreviewTypes);
  const isBundle = type === "bundle";
  const mustViewDetails =
    isService ||
    isBundle ||
    selectedChild.has_required_addons ||
    selectedChild.has_term_and_condition ||
    selectedChild.has_required_fields ||
    (!!units.length && show_all_product_units);

  const viewDetails =
    mustViewDetails ||
    selectedChild.is_configurable ||
    selectedChild.has_configurable_parent;

  const isDisabled = !!selectedChild.is_disable;
  const outOfStock =
    selectedChild.quantity_on_hand - currentProductInCart.quantity <= 0 ||
    (!!selectedChild.min_quantity_in_cart &&
      selectedChild.min_quantity_in_cart >
        selectedChild.quantity_on_hand - currentProductInCart.quantity);
  const handleSelectedChildIndex = useCallback(
    (index: number | null) => {
      setSelectedChildIndex(index);
      if (!!bundleSetSelectedChildIndex) bundleSetSelectedChildIndex(index);
    },
    [bundleSetSelectedChildIndex],
  );
  const value = useMemo(
    () => ({
      selectedChild,
      selectedChildIndex,
      setSelectedChildIndex: handleSelectedChildIndex,
      isSelectedChild: selectedChildIndex !== null && selectedChildIndex >= 0,
      product,
      attrs_values: intersectionsArray(
        product.attribute_ids,
        selectedChild?.attribute_ids,
      ),
      quantity,
      setQuantity,
      addedToCart,
      setAddedToCart,
      quick_variant_selection,
      selectedAttributes,
      setSelectedAttributes,
      showselectAttrsPopup:
        product?.attributes_builder?.length > 0 &&
        !selectedChildIndex &&
        selectedAttributes?.length > 0,
      outOfStock,
      isDisabled,
      viewDetails,
      mustViewDetails,
      currentProductInCart,
      allow_user_request_stock_availability_reminder:
        allow_user_request_stock_availability_reminder &&
        !bundleSetSelectedChildIndex,
      showRate: product_reviews_visibility !== ReviewVisibility.none,
    }),
    [
      selectedChild,
      selectedChildIndex,
      handleSelectedChildIndex,
      product,
      quantity,
      addedToCart,
      quick_variant_selection,
      selectedAttributes,
      outOfStock,
      isDisabled,
      viewDetails,
      mustViewDetails,
      currentProductInCart,
      allow_user_request_stock_availability_reminder,
      bundleSetSelectedChildIndex,
      product_reviews_visibility,
    ],
  );

  return (
    <ProductBoxContext.Provider value={value}>
      {children}
    </ProductBoxContext.Provider>
  );
};

export const useProductBox = (): ProductBoxContextType => {
  const context = useContext(ProductBoxContext);
  if (context === undefined) {
    throw new Error("useProductBox must be used within a ProductBoxProvider");
  }
  return context;
};
